import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { BranchInfo } from '../api'
import { PageDescription } from '../components/PageDescription'

const BRANCH_TYPE_STYLES: Record<string, { dot: string; badge: string }> = {
    main:    { dot: 'bg-emerald-400',  badge: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30' },
    sprint:  { dot: 'bg-indigo-400',   badge: 'bg-indigo-500/20 text-indigo-300 ring-1 ring-indigo-500/30' },
    release: { dot: 'bg-blue-400',     badge: 'bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/30' },
    feature: { dot: 'bg-violet-400',   badge: 'bg-violet-500/20 text-violet-300 ring-1 ring-violet-500/30' },
    fix:     { dot: 'bg-amber-400',    badge: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30' },
    hotfix:  { dot: 'bg-red-400',      badge: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30' },
    other:   { dot: 'bg-slate-500',    badge: 'bg-slate-500/20 text-slate-400 ring-1 ring-slate-500/30' },
}

const LANE_COLORS: Record<string, { stroke: string; fill: string; label: string }> = {
    main:    { stroke: '#34d399', fill: '#34d399', label: '#6ee7b7' },
    sprint:  { stroke: '#818cf8', fill: '#818cf8', label: '#a5b4fc' },
    release: { stroke: '#60a5fa', fill: '#60a5fa', label: '#93c5fd' },
    feature: { stroke: '#a78bfa', fill: '#a78bfa', label: '#c4b5fd' },
    fix:     { stroke: '#fbbf24', fill: '#fbbf24', label: '#fcd34d' },
    hotfix:  { stroke: '#f87171', fill: '#f87171', label: '#fca5a5' },
    other:   { stroke: '#64748b', fill: '#64748b', label: '#94a3b8' },
}

// Lane order and which lane each type branches from
const LANE_DEFS: { type: string; parent: string | null }[] = [
    { type: 'main',    parent: null },
    { type: 'sprint',  parent: 'main' },
    { type: 'release', parent: 'main' },
    { type: 'feature', parent: 'sprint' },
    { type: 'fix',     parent: 'main' },
    { type: 'hotfix',  parent: 'release' },
    { type: 'other',   parent: 'main' },
]

// ── Layout constants ──────────────────────────────────────────────────────────

const LANE_GAP   = 62    // px between lane centre lines
const LEFT_PAD   = 90    // px for lane labels on left
const RIGHT_PAD  = 48    // px trailing space
const TOP_PAD    = 40    // px above first lane
const BOTTOM_PAD = 48    // px below last lane
const DOT_R      = 5     // commit dot radius
const SLOT_W     = 58    // px between consecutive branch slots in the same lane

interface BranchNode {
    branch: BranchInfo
    laneIdx: number
    parentLaneIdx: number
    x: number           // centre x of branch dot
    isMerged: boolean
    labelBelow: boolean // alternated per slot index to prevent vertical crowding
}

// ── Graph builder ─────────────────────────────────────────────────────────────

function buildGraph(branches: BranchInfo[]): {
    nodes: BranchNode[]
    activeLanes: string[]
    svgW: number
    svgH: number
} {
    if (branches.length === 0) return { nodes: [], activeLanes: [], svgW: 300, svgH: 100 }

    const typeSet     = new Set(branches.map(b => b.branchType))
    const activeLanes = LANE_DEFS.filter(d => typeSet.has(d.type)).map(d => d.type)
    const laneIndex   = Object.fromEntries(activeLanes.map((t, i) => [t, i]))

    // Resolve each branch type's parent lane index
    const parentIdx: Record<string, number> = {}
    for (const def of LANE_DEFS) {
        const pLane = def.parent
        parentIdx[def.type] = pLane !== null && laneIndex[pLane] !== undefined
            ? laneIndex[pLane]!
            : laneIndex[def.type] ?? 0
    }

    // Group branches by type, sorted by commit date (oldest first = leftmost)
    const byType = new Map<string, BranchInfo[]>()
    for (const b of branches) {
        if (!byType.has(b.branchType)) byType.set(b.branchType, [])
        byType.get(b.branchType)!.push(b)
    }
    for (const list of byType.values()) {
        list.sort((a, b) => new Date(a.committedAt).getTime() - new Date(b.committedAt).getTime())
    }

    const nodes: BranchNode[] = []
    let maxX = LEFT_PAD

    for (const [type, list] of byType) {
        const li  = laneIndex[type] ?? 0
        const pli = parentIdx[type] ?? 0
        list.forEach((b, i) => {
            const x = LEFT_PAD + i * SLOT_W + SLOT_W / 2
            if (x > maxX) maxX = x
            const isMerged   = b.behindCount === 0 && b.aheadCount > 0 && !b.isStale
            const labelBelow = i % 2 === 0
            nodes.push({ branch: b, laneIdx: li, parentLaneIdx: pli, x, isMerged, labelBelow })
        })
    }

    const svgW = maxX + SLOT_W * 0.5 + RIGHT_PAD
    const svgH = TOP_PAD + activeLanes.length * LANE_GAP + BOTTOM_PAD

    return { nodes, activeLanes, svgW, svgH }
}

function laneY(laneIdx: number): number {
    return TOP_PAD + laneIdx * LANE_GAP + LANE_GAP / 2
}

// ── Diagram component with zoom / pan ─────────────────────────────────────────

function BranchingDiagram({ branches }: { branches: BranchInfo[] }) {
    const { nodes, activeLanes, svgW } = buildGraph(branches)
    const [xf, setXf]     = useState({ scale: 1, dx: 8, dy: 8 })
    const [panning, setPanning] = useState(false)
    const panOrigin = useRef({ x: 0, y: 0, dx: 0, dy: 0 })
    const containerRef = useRef<HTMLDivElement>(null)

    // Non-passive wheel listener for zoom centred on cursor
    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        function onWheel(e: WheelEvent) {
            e.preventDefault()
            const rect   = el!.getBoundingClientRect()
            const mx     = e.clientX - rect.left
            const my     = e.clientY - rect.top
            const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12
            setXf(t => {
                const ns = Math.max(0.2, Math.min(8, t.scale * factor))
                return { scale: ns, dx: mx + (t.dx - mx) * (ns / t.scale), dy: my + (t.dy - my) * (ns / t.scale) }
            })
        }
        el.addEventListener('wheel', onWheel, { passive: false })
        return () => el.removeEventListener('wheel', onWheel)
    }, [])

    // Compute each lane's x span from its branch nodes
    const laneSpans = activeLanes.map((_, li) => {
        const laneNodes = nodes.filter(n => n.laneIdx === li)
        if (laneNodes.length === 0) return { min: LEFT_PAD, max: LEFT_PAD }
        return { min: Math.min(...laneNodes.map(n => n.x)), max: Math.max(...laneNodes.map(n => n.x)) }
    })

    return (
        <div
            ref={containerRef}
            className="relative rounded-xl border border-slate-700 bg-slate-800/40 overflow-hidden"
            style={{ height: 380, userSelect: 'none', cursor: panning ? 'grabbing' : 'grab' }}
            onMouseDown={e => {
                setPanning(true)
                panOrigin.current = { x: e.clientX, y: e.clientY, dx: xf.dx, dy: xf.dy }
            }}
            onMouseMove={e => {
                if (!panning) return
                setXf(t => ({
                    ...t,
                    dx: panOrigin.current.dx + e.clientX - panOrigin.current.x,
                    dy: panOrigin.current.dy + e.clientY - panOrigin.current.y,
                }))
            }}
            onMouseUp={() => setPanning(false)}
            onMouseLeave={() => setPanning(false)}
        >
            <svg width="100%" height="100%" aria-label="Branch lane diagram">
                <defs>
                    {activeLanes.map(t => {
                        const c = LANE_COLORS[t]?.stroke ?? '#64748b'
                        return (
                            <marker key={t} id={`arr-${t}`} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                                <path d="M0,0 L0,6 L6,3 z" fill={c} opacity={0.85} />
                            </marker>
                        )
                    })}
                </defs>

                <g transform={`translate(${xf.dx},${xf.dy}) scale(${xf.scale})`}>

                    {/* Lane spines */}
                    {activeLanes.map((type, li) => {
                        const y      = laneY(li)
                        const colors = LANE_COLORS[type]!
                        const span   = laneSpans[li]!
                        const isMain = type === 'main'
                        const x1     = isMain ? LEFT_PAD         : span.min - SLOT_W * 0.4
                        const x2     = isMain ? svgW - RIGHT_PAD : span.max + SLOT_W * 0.4

                        return (
                            <g key={type}>
                                <text
                                    x={LEFT_PAD - 10} y={y}
                                    fill={colors.label} fontSize={10}
                                    textAnchor="end" dominantBaseline="middle"
                                    fontFamily="ui-monospace, monospace" fontWeight="600" letterSpacing="0.04em"
                                >
                                    {type}
                                </text>
                                <line
                                    x1={x1} y1={y} x2={x2} y2={y}
                                    stroke={colors.stroke}
                                    strokeWidth={isMain ? 2 : 1.5}
                                    strokeOpacity={isMain ? 0.55 : 0.3}
                                    strokeDasharray={isMain ? undefined : '5 4'}
                                />
                            </g>
                        )
                    })}

                    {/* Branch nodes: fork line + dot + labels */}
                    {nodes.map(node => {
                        const { branch, laneIdx, parentLaneIdx, x, isMerged, labelBelow } = node
                        const colors  = LANE_COLORS[branch.branchType]!
                        const cy      = laneY(laneIdx)
                        const py      = laneY(parentLaneIdx)
                        const isMain  = branch.branchType === 'main'
                        const opacity = branch.isStale ? 0.28 : 1

                        const stripped = branch.name.replace(/^(feature|fix|hotfix|sprint|release)\//, '')
                        const label    = stripped.length > 18 ? stripped.slice(0, 17) + '…' : stripped

                        // Label placement alternates above/below to reduce horizontal crowding
                        const labelY = labelBelow ? cy + DOT_R + 13 : cy - DOT_R - 14
                        const shaY   = labelBelow ? labelY + 11      : labelY - 10

                        // Fork line curves from parent lane Y down (or up) to just above/below the dot
                        const forkMidY = py + (cy - py) * 0.6
                        const dotEdge  = cy < py ? cy + DOT_R + 1 : cy - DOT_R - 1

                        return (
                            <g key={branch.name} opacity={opacity}>
                                {/* Fork line: parent lane → dot */}
                                {!isMain && laneIdx !== parentLaneIdx && (
                                    <path
                                        d={`M ${x} ${py} C ${x} ${forkMidY} ${x} ${forkMidY} ${x} ${dotEdge}`}
                                        fill="none"
                                        stroke={colors.stroke}
                                        strokeWidth={1}
                                        strokeOpacity={0.4}
                                    />
                                )}

                                {/* Merge arrow: dot → parent lane (dashed) */}
                                {!isMain && isMerged && laneIdx !== parentLaneIdx && (
                                    <path
                                        d={`M ${x} ${cy < py ? cy + DOT_R + 1 : cy - DOT_R - 1} C ${x} ${cy + (py - cy) * 0.4} ${x} ${py - 4} ${x} ${py}`}
                                        fill="none"
                                        stroke={colors.stroke}
                                        strokeWidth={1}
                                        strokeOpacity={0.55}
                                        strokeDasharray="3 2"
                                        markerEnd={`url(#arr-${branch.branchType})`}
                                    />
                                )}

                                {/* Branch dot */}
                                <circle
                                    cx={x} cy={cy} r={DOT_R}
                                    fill={branch.isStale ? '#1e293b' : colors.fill}
                                    stroke={colors.stroke}
                                    strokeWidth={branch.isStale ? 1.5 : 1}
                                />

                                {/* Merged check — opposite side to label */}
                                {isMerged && (
                                    <text
                                        x={x} y={labelBelow ? cy - DOT_R - 5 : cy + DOT_R + 10}
                                        fill="#34d399" fontSize={7}
                                        textAnchor="middle" fontFamily="ui-monospace, monospace" opacity={0.85}
                                    >
                                        ✓
                                    </text>
                                )}

                                {/* Branch name label */}
                                <text
                                    x={x} y={labelY}
                                    fill={colors.label} fontSize={8.5}
                                    textAnchor="middle" dominantBaseline={labelBelow ? 'hanging' : 'auto'}
                                    fontFamily="ui-monospace, monospace" opacity={0.9}
                                >
                                    {label}
                                </text>

                                {/* Commit SHA */}
                                <text
                                    x={x} y={shaY}
                                    fill="#475569" fontSize={7}
                                    textAnchor="middle" fontFamily="ui-monospace, monospace"
                                >
                                    {branch.shortSha}
                                </text>
                            </g>
                        )
                    })}
                </g>
            </svg>

            {/* Reset zoom */}
            <button
                onClick={() => setXf({ scale: 1, dx: 8, dy: 8 })}
                className="absolute bottom-3 right-3 rounded-lg bg-slate-700/70 px-2.5 py-1 text-[10px] font-medium text-slate-400 hover:text-slate-200 hover:bg-slate-600/80 transition-colors backdrop-blur-sm"
            >
                Reset
            </button>

            {/* Legend */}
            <div className="absolute bottom-3 left-3 flex items-center gap-3 pointer-events-none">
                <span className="text-[9px] text-slate-600 uppercase tracking-wider font-medium">Fork</span>
                <svg width="20" height="16" className="opacity-50">
                    <line x1="10" y1="2" x2="10" y2="14" stroke="#94a3b8" strokeWidth="1" />
                </svg>
                <span className="text-[9px] text-slate-600 uppercase tracking-wider font-medium">Merged</span>
                <svg width="20" height="16" className="opacity-60">
                    <line x1="10" y1="2" x2="10" y2="14" stroke="#34d399" strokeWidth="1" strokeDasharray="3 2" />
                </svg>
                <span className="text-[9px] text-slate-500">Scroll to zoom · drag to pan</span>
            </div>
        </div>
    )
}

// ── Supporting components ─────────────────────────────────────────────────────

function relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60_000)
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
}

function AheadBehindBar({ ahead, behind }: { ahead: number; behind: number }) {
    const maxVal = Math.max(ahead, behind, 1)
    return (
        <div className="flex items-center gap-1.5 w-36">
            {behind > 0 && (
                <div className="flex items-center gap-1 shrink-0">
                    <div
                        className="h-1.5 rounded-full bg-slate-600"
                        style={{ width: `${Math.round((behind / maxVal) * 48)}px` }}
                    />
                    <span className="text-[10px] text-slate-500">-{behind}</span>
                </div>
            )}
            <div className="h-3 w-px bg-slate-700 shrink-0" />
            {ahead > 0 && (
                <div className="flex items-center gap-1 shrink-0">
                    <span className="text-[10px] text-emerald-500">+{ahead}</span>
                    <div
                        className="h-1.5 rounded-full bg-emerald-600/60"
                        style={{ width: `${Math.round((ahead / maxVal) * 48)}px` }}
                    />
                </div>
            )}
            {ahead === 0 && behind === 0 && (
                <span className="text-[10px] text-emerald-500">in sync</span>
            )}
        </div>
    )
}

function BranchRow({ branch }: { branch: BranchInfo }) {
    const styles = BRANCH_TYPE_STYLES[branch.branchType] ?? BRANCH_TYPE_STYLES['other']!
    return (
        <div className={`group flex items-center gap-4 px-5 py-2.5 border-b border-slate-700/30 last:border-0 ${branch.isStale ? 'opacity-50' : ''}`}>
            <span className={`w-2 h-2 rounded-full shrink-0 ${styles.dot}`} />
            <span className="font-mono text-sm text-slate-200 truncate flex-1 min-w-0">{branch.name}</span>
            <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 shrink-0 ${styles.badge}`}>{branch.branchType}</span>
            {branch.isStale && (
                <span className="text-[10px] rounded-full px-2 py-0.5 bg-slate-700/50 text-slate-500 shrink-0">stale</span>
            )}
            <AheadBehindBar ahead={branch.aheadCount} behind={branch.behindCount} />
            <span className="text-xs text-slate-500 truncate w-48 hidden md:block shrink-0">{branch.commitMessage}</span>
            <span className="font-mono text-[10px] text-slate-600 shrink-0 w-16 text-right">{branch.shortSha}</span>
            <span className="text-xs text-slate-500 shrink-0 w-16 text-right">{relativeTime(branch.committedAt)}</span>
        </div>
    )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function BranchingDiagramPage() {
    const [typeFilter, setTypeFilter] = useState<string>('all')
    const [view, setView] = useState<'list' | 'diagram'>('list')

    const { data, isLoading, isError } = useQuery({
        queryKey: ['branches'],
        queryFn: releaseApi.getBranches,
        staleTime: 60_000,
    })

    const allTypes = ['main', 'sprint', 'release', 'feature', 'fix', 'hotfix', 'other']

    const branches = (data?.branches ?? []).filter(
        b => typeFilter === 'all' || b.branchType === typeFilter
    )

    const branchCounts = (data?.branches ?? []).reduce<Record<string, number>>((acc, b) => {
        acc[b.branchType] = (acc[b.branchType] ?? 0) + 1
        return acc
    }, {})

    return (
        <div className="flex flex-col min-h-0 flex-1">
            <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-4">
                    <div>
                        <span className="text-sm font-semibold text-slate-200">Branches</span>
                        <PageDescription
                            subtitle="Live git branch state"
                            storageKey="desc-branches"
                            detail="Shows all branches in the repository with ahead/behind counts relative to main. Stale branches (no commits in 14 days) are de-emphasised. Use this to verify which feature branches are ahead of main and which release branches contain the expected commits."
                        />
                    </div>

                    {/* View toggle */}
                    <div className="ml-auto flex items-center gap-1 bg-slate-800 rounded-lg p-0.5">
                        <button
                            onClick={() => setView('list')}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${view === 'list' ? 'bg-slate-700 text-slate-100' : 'text-slate-500 hover:text-slate-300'}`}
                            title="List view"
                        >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                                <path d="M2 4h12M2 8h12M2 12h12" />
                            </svg>
                            List
                        </button>
                        <button
                            onClick={() => setView('diagram')}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${view === 'diagram' ? 'bg-slate-700 text-slate-100' : 'text-slate-500 hover:text-slate-300'}`}
                            title="Diagram view"
                        >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                                <path d="M2 8h4M10 8h4M6 8a2 2 0 1 0 4 0 2 2 0 0 0-4 0zM4 4V2M4 14v-2M12 4V2M12 14v-2" />
                            </svg>
                            Diagram
                        </button>
                    </div>

                    {data && (
                        <span className="text-[10px] text-slate-600">
                            {data.branches.length} branches · updated {relativeTime(data.fetchedAt)}
                        </span>
                    )}
                </div>
            </header>

            <main className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
                <div className="max-w-5xl mx-auto flex flex-col gap-4">
                    {/* Type filter pills */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={() => setTypeFilter('all')}
                            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${typeFilter === 'all' ? 'bg-slate-700 text-slate-100' : 'text-slate-500 hover:text-slate-300'}`}
                        >
                            All ({data?.branches.length ?? 0})
                        </button>
                        {allTypes.map(t => {
                            const count = branchCounts[t] ?? 0
                            if (count === 0) return null
                            const styles = BRANCH_TYPE_STYLES[t] ?? BRANCH_TYPE_STYLES['other']!
                            return (
                                <button
                                    key={t}
                                    onClick={() => setTypeFilter(t)}
                                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${typeFilter === t ? styles.badge : 'text-slate-500 hover:text-slate-300'}`}
                                >
                                    {t} ({count})
                                </button>
                            )
                        })}
                    </div>

                    {isLoading ? (
                        <div className="py-16 text-center text-slate-500 text-sm animate-pulse">Loading branches…</div>
                    ) : isError ? (
                        <div className="py-16 text-center text-red-400 text-sm">Failed to load branches</div>
                    ) : branches.length === 0 ? (
                        <div className="py-16 text-center text-slate-500 text-sm">No branches</div>
                    ) : view === 'diagram' ? (
                        <BranchingDiagram branches={branches} />
                    ) : (
                        <div className="rounded-xl border border-slate-700 bg-slate-800/40 overflow-hidden">
                            <div className="flex items-center gap-4 px-5 py-2 border-b border-slate-700/60 bg-slate-900/40">
                                <span className="w-2 shrink-0" />
                                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 flex-1">Branch</span>
                                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 w-16 shrink-0">Type</span>
                                <span className="w-12 shrink-0" />
                                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 w-36 shrink-0">Ahead/behind main</span>
                                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 w-48 hidden md:block shrink-0">Last commit</span>
                                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 w-16 text-right shrink-0">SHA</span>
                                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 w-16 text-right shrink-0">Age</span>
                            </div>
                            {branches.map(b => <BranchRow key={b.name} branch={b} />)}
                        </div>
                    )}

                    {/* Legend (list view only) */}
                    {view === 'list' && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1.5 px-1">
                            <span className="text-[10px] text-slate-600 uppercase tracking-wider font-medium shrink-0 self-center">Legend</span>
                            {allTypes.map(t => {
                                const styles = BRANCH_TYPE_STYLES[t] ?? BRANCH_TYPE_STYLES['other']!
                                return (
                                    <span key={t} className="flex items-center gap-1.5">
                                        <span className={`w-2 h-2 rounded-full ${styles.dot}`} />
                                        <span className="text-[10px] text-slate-500">{t}</span>
                                    </span>
                                )
                            })}
                        </div>
                    )}
                </div>
            </main>
        </div>
    )
}
